import React, { useEffect, useMemo } from 'react';

import { ExpLabel, getExpMessage } from '@exp/components/localization';
import { useIsMediaMD } from '@exp/hooks/useMediaQuery';

import { ModeConfig, ModeContext } from '../constants/modes';
import AccountTeasers from '../components/AccountTeasers';
import AffiliationListing from '../components/AffiliationListing';
import AffilationTeasers from '../components/AffiliationTeasers';
import Stepper from '../components/Stepper';
import VetsBackdrop from '../components/VetsBackdrop';
import VetTeasers from '../components/VetsTeasers';
import {
  BackdropStage,
  BackdropTabs,
  selectBackdrop,
  selectBackdropTab,
  selectConfig,
  selectContext,
  selectShowManageBackdrop,
  setBackdropTab,
} from '../redux/context';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { filterAffiliations, isVetCode } from '../utilities/utilities';

import './Backdrop.less';

type BackdropProps = {
  completedCount?: number;
  stepCount?: number;
};

const SetupBackdrop = ({ completedCount, stepCount }: BackdropProps) => {
  const { user }: ModeContext = useAppSelector(selectContext);
  const { activeCount, expiredCount, pendingCount } = window.Exp?.affiliations || {};

  return (
    <div className="modal-takeover-backdrop setup-stage">
      <header className="modal-takeover-setup-header">
        <ExpLabel
          defaultMessage="Welcome to ExpertVoice, {name}!"
          messageKey="onb.name.heading"
          tagName="p"
          values={{
            name:
              user?.firstName ||
              getExpMessage({ defaultMessage: 'Expert', messageKey: 'onb.backdrop.setup.name-fallback' }),
          }}
        />
        <ExpLabel
          defaultMessage="Let's help you access some deals."
          tagName="h2"
          messageKey="onb.backdrop.setup.headline"
        />
      </header>
      <div className="stepper-container">
        <Stepper
          active
          label={getExpMessage({
            defaultMessage: 'Set Up',
            messageKey: 'onb.stepper.title.setup',
          })}
          segments={stepCount}
          segmentsComplete={completedCount}
          prefill
        />
        <Stepper
          label={getExpMessage({
            defaultMessage: 'Qualify',
            messageKey: 'onb.stepper.title.qualify',
          })}
          prefill={!!(activeCount || expiredCount || pendingCount)}
          segments={2}
          segmentsComplete={activeCount + expiredCount ? 2 : pendingCount ? 1 : 0}
        />
      </div>
      <ExpLabel
        defaultMessage="To snag the best deals, we need a few details from you."
        messageKey="onb.backdrop.setup.text"
      />
      <AffilationTeasers />
    </div>
  );
};

SetupBackdrop.displayName = 'SetupBackdrop';

const QualifyBackdrop = ({ completedCount, stepCount }: BackdropProps) => {
  const { groupCodeResult, pendingApplication }: ModeContext = useAppSelector(selectContext);
  const { groupCode } = groupCodeResult || {};
  const isMD = useIsMediaMD();
  const isVAPass = useMemo(() => pendingApplication && isVetCode(groupCode), [groupCode, pendingApplication]);

  return (
    <div className={`modal-takeover-backdrop qualify-stage ${isVAPass ? 'va-qualify' : ''}`}>
      <div className="stepper-container">
        <Stepper
          label={getExpMessage({
            defaultMessage: 'Set Up',
            messageKey: 'onb.stepper.title.setup',
          })}
          segments={1}
          segmentsComplete={1}
          prefill
        />
        <Stepper
          active
          label={getExpMessage({
            defaultMessage: 'Qualify',
            messageKey: 'onb.stepper.title.qualify',
          })}
          segments={stepCount}
          segmentsComplete={completedCount}
          prefill
        />
      </div>
      {isMD && isVAPass && <VetTeasers />}
      {stepCount === completedCount ? (
        <ExpLabel
          className="more-tease"
          tagName="p"
          defaultMessage="The more teams you join, the more brands and exclusive deals you could unlock."
          messageKey="onb.backdrop.qualified.text"
        />
      ) : (
        <p>
          <ExpLabel
            className="lede"
            defaultMessage="You’re almost there!"
            messageKey="onb.backdrop.qualify.text-lede"
          />
          <ExpLabel
            defaultMessage="All we need is some proof. Like showing your ID at a VIP event, unlock benefits personal to you based on your job, role or industry."
            messageKey="onb.backdrop.qualify.text"
          />
        </p>
      )}
      <AffilationTeasers />
    </div>
  );
};

QualifyBackdrop.displayName = 'QualifyBackdrop';

const ManageBackdrop = () => {
  const dispatch = useAppDispatch();
  const isMD = useIsMediaMD();
  const backdropTab = useAppSelector(selectBackdropTab);
  const { affiliations }: ModeContext = useAppSelector(selectContext);
  const affiliationsToDisplay = useMemo(() => affiliations?.filter(filterAffiliations), [affiliations]);

  return (
    <div className="modal-takeover-backdrop manage-stage qualify-stage">
      {!isMD && (
        <div className="aff-toggle-tabs tabs full">
          <div
            className={`tab aff-list-tab ${!backdropTab || backdropTab === BackdropTabs.listing ? 'selected' : ''}`}
            onClick={() => {
              dispatch(setBackdropTab(BackdropTabs.listing));
              // Save the selected tab to browser history
              window.history.pushState(
                { ...window.history.state, backdropTab: BackdropTabs.listing },
                '',
                window.location.pathname,
              );
            }}
          >
            <p>
              <i className="exp-ux-small exp-ux-groups" />
              <ExpLabel defaultMessage="My Teams" messageKey="onb.backdrop.tab.affListing" />
            </p>
          </div>
          <div
            className={`tab qualify-tab ${backdropTab === BackdropTabs.qualify ? 'selected' : ''}`}
            onClick={() => {
              dispatch(setBackdropTab(BackdropTabs.qualify));
              // Save the selected tab to browser history
              window.history.pushState(
                { ...window.history.state, backdropTab: BackdropTabs.qualify },
                '',
                window.location.pathname,
              );
            }}
          >
            <p>
              <i className="exp-ux-small exp-ux-search" />
              <ExpLabel defaultMessage="Explore All Teams" messageKey="onb.backdrop.tab.qualify" />
            </p>
          </div>
        </div>
      )}
      <AffiliationListing />
      {(affiliationsToDisplay?.length ?? 0) < 3 && <AffilationTeasers />}
    </div>
  );
};

ManageBackdrop.displayName = 'ManageBackdrop';

const PostAffiliationBackdrop = () => {
  const { selectedTeam }: ModeContext = useAppSelector(selectContext);
  const { activeCount } = window.Exp?.affiliations || {};

  return (
    <div className="modal-takeover-backdrop approved-stage">
      <div className="stepper-container">
        {activeCount < 2 && (
          <Stepper
            label={getExpMessage({
              defaultMessage: 'Set Up',
              messageKey: 'onb.stepper.title.setup',
            })}
            segments={1}
            segmentsComplete={1}
            prefill
          />
        )}
        <Stepper
          active
          label={getExpMessage({
            defaultMessage: 'Qualify',
            messageKey: 'onb.stepper.title.qualify',
          })}
          segments={1}
          segmentsComplete={1}
          prefill
        />
      </div>
      <ExpLabel
        tagName="p"
        defaultMessage="Congrats! Your {team} qualifications are approved."
        messageKey="onb.backdrop.postAffiliation.text"
        values={{ team: selectedTeam?.team?.name || '' }}
      />
    </div>
  );
};

PostAffiliationBackdrop.displayName = 'PostAffiliationBackdrop';

const InitialBackdrop = () => (
  <div className="modal-takeover-backdrop initial-stage">
    <ExpLabel
      defaultMessage="Earn deals on products that fuel your passions"
      tagName="h2"
      messageKey="onb.backdrop.initial.headline"
    />
    <AccountTeasers />
  </div>
);

InitialBackdrop.displayName = 'InitialBackdrop';

const Backdrop = ({ stepCount = 0, completedCount = 0 }: BackdropProps) => {
  const dispatch = useAppDispatch();
  const stage = useAppSelector(selectBackdrop);
  const { theme }: ModeConfig = useAppSelector(selectConfig);
  const showManage = useAppSelector(selectShowManageBackdrop);

  // Only ManageBackdrop renders the tab strip, so only it may leave the tab on
  // `listing`. Gating this on anything looser — an unfiltered affiliation count,
  // say — strands mobile users on a `manage-listing` layout that hides the
  // content pane and offers no tab to switch back with (TPF-65908).
  useEffect(() => {
    if (showManage) {
      // Restore the previously selected tab from browser history state, or default to listing
      const savedTab = window.history.state?.backdropTab;
      const tabToSet = savedTab || BackdropTabs.listing;
      dispatch(setBackdropTab(tabToSet));
    } else {
      dispatch(setBackdropTab(BackdropTabs.qualify));
    }
  }, [dispatch, showManage]);

  return (
    <>
      {stage === BackdropStage.setup && <SetupBackdrop completedCount={completedCount} stepCount={stepCount} />}
      {stage === BackdropStage.qualify && !showManage && (
        <QualifyBackdrop completedCount={completedCount} stepCount={stepCount} />
      )}
      {showManage && <ManageBackdrop />}
      {stage === BackdropStage.postAffiliation && <PostAffiliationBackdrop />}
      {stage === BackdropStage.initial && (theme === 'vets' ? <VetsBackdrop /> : <InitialBackdrop />)}
    </>
  );
};

Backdrop.displayName = 'Backdrop';

export default Backdrop;
